import React, { useState, useEffect } from 'react';
import PhoneInput from 'react-phone-number-input';
import { AsYouType } from 'libphonenumber-js';
import 'react-phone-number-input/style.css';
import './Form.css';

const DEFAULT_COUNTRY = 'IN';
const MAX_NATIONAL_DIGITS = 12;
const MIN_NATIONAL_DIGITS = 7;

function getNationalNumberInfo(phone, defaultCountry = DEFAULT_COUNTRY) {
  const asYouType = new AsYouType(defaultCountry);
  asYouType.input(phone || '');
  const callingCode = asYouType.getCallingCode();
  const national = asYouType.getNationalNumber() || '';
  return { callingCode, national };
}

/** Trim to at most maxNational digits after country code. Fallback: digit-based when AsYouType doesn't detect country. */
function trimPhoneToMaxNationalDigits(phone, defaultCountry = DEFAULT_COUNTRY, maxNational = MAX_NATIONAL_DIGITS) {
  const str = String(phone || '').trim();
  if (!str) return '';

  const { callingCode, national } = getNationalNumberInfo(str, defaultCountry);
  if (callingCode && national.length > maxNational) {
    return '+' + callingCode + national.slice(0, maxNational);
  }
  // Fallback: digits only, assume +91 (2 digits) + max 12 national = 14 digits total for default IN
  const digits = str.replace(/\D/g, '');
  if (digits.length > 14 && digits.startsWith('91')) {
    return '+' + digits.slice(0, 2 + maxNational);
  }
  if (digits.length > 15) {
    // Other country: allow up to 3-digit code + 12 national = 15
    const codeLen = digits.startsWith('1') ? 1 : digits.startsWith('7') ? 1 : 2;
    if (digits.length > codeLen + maxNational) {
      return '+' + digits.slice(0, codeLen + maxNational);
    }
  }
  return str;
}

function getNationalDigitCount(phone, defaultCountry = DEFAULT_COUNTRY) {
  const { national } = getNationalNumberInfo(phone, defaultCountry);
  return national.length;
}

const IconPhone = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const IconMail = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const IconAddress = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const defaultContact = {
  address: 'InfinySkills, Mahaveer Nagar, Mansarovar, Jaipur, Rajasthan, India, 302020',
  phone: '+91 9256479304',
  email: 'khyati@infinyskills.com',
};

const defaultIntro = 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.';

const defaultPrivacy = 'By submitting, you agree to the processing of your personal data as described in the Privacy Statement.';

const Form = ({
  title,
  subtitle,
  onSubmit,
  id = 'contact-form',
  phone,
  email,
  address,
  introText,
  privacyText,
  submitStatus,
  successMessage = 'Thank you! Your message has been sent.',
  errorMessage = 'Something went wrong. Please try again.',
  variant, // 'modal' = only form card, no section/details
  showOrganisationField = false, // only true for popup form
}) => {
  const isModal = variant === 'modal';
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    ...(showOrganisationField ? { organisation: '' } : {}),
  });
  const [errorField, setErrorField] = useState(null); // 'name' | 'email' | 'phone' | 'organisation' | 'email_format' | 'phone_length' | null

  const fieldErrorMessages = {
    name: 'Name is required.',
    email: 'Email is required.',
    phone: 'Phone number is required.',
    organisation: 'Organisation name is required.',
    email_format: 'Please enter a valid email address.',
    phone_length: 'Phone number must be 7–12 digits (excluding country code).',
  };
  const fieldError = errorField ? fieldErrorMessages[errorField] : null;

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim());

  // Enforce max 12 digits in national number (excluding country code); sync on every change so UI never shows more
  useEffect(() => {
    const p = formData.phone;
    if (!p) return;
    const trimmed = trimPhoneToMaxNationalDigits(p, DEFAULT_COUNTRY, MAX_NATIONAL_DIGITS);
    if (trimmed !== p) {
      setFormData((prev) => ({ ...prev, phone: trimmed }));
    }
  }, [formData.phone]);

  const contact = {
    address: address || defaultContact.address,
    phone: phone || defaultContact.phone,
    email: email || defaultContact.email,
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errorField && (name === 'name' || name === 'email' || name === 'organisation' || name === 'phone')) {
      setErrorField(null);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrorField(null);
    const trimmedName = (formData.name || '').trim();
    const trimmedEmail = (formData.email || '').trim();
    const trimmedPhone = (formData.phone || '').trim();
    const trimmedOrganisation = (formData.organisation || '').trim();
    if (!trimmedName) {
      setErrorField('name');
      return;
    }
    if (!trimmedEmail) {
      setErrorField('email');
      return;
    }
    if (!isValidEmail(trimmedEmail)) {
      setErrorField('email_format');
      return;
    }
    if (!trimmedPhone) {
      setErrorField('phone');
      return;
    }
    const nationalDigits = getNationalDigitCount(trimmedPhone, DEFAULT_COUNTRY);
    if (nationalDigits < MIN_NATIONAL_DIGITS || nationalDigits > MAX_NATIONAL_DIGITS) {
      setErrorField('phone_length');
      return;
    }
    if (showOrganisationField && !trimmedOrganisation) {
      setErrorField('organisation');
      return;
    }
    if (onSubmit) {
      onSubmit(formData);
      setFormData({ name: '', email: '', phone: '', message: '', ...(showOrganisationField ? { organisation: '' } : {}) });
    } else {
      console.log('Form submitted:', formData);
      setFormData({ name: '', email: '', phone: '', message: '', ...(showOrganisationField ? { organisation: '' } : {}) });
    }
  };

  const formContent = (
    <>
      <div className={isModal ? 'common-form-layout common-form-layout--modal' : 'common-form-layout'}>
        {!isModal && (
          <div className="common-form-details">
            <h2 className="common-form-details-title">Get In Touch</h2>
            <p className="common-form-details-intro">
              {introText || subtitle || defaultIntro}
            </p>
            <div className="common-form-details-list">
              <div className="common-form-detail-item">
                <span className="common-form-detail-icon">
                  <IconAddress />
                </span>
                <div className="common-form-detail-content">
                  <span className="common-form-detail-label">Address</span>
                  <span className="common-form-detail-value common-form-detail-address">
                    {contact.address}
                  </span>
                </div>
              </div>
              <div className="common-form-detail-item">
                <span className="common-form-detail-icon">
                  <IconPhone />
                </span>
                <div className="common-form-detail-content">
                  <span className="common-form-detail-label">Phone Number</span>
                  <a href={`tel:${contact.phone.replace(/\s/g, '')}`} className="common-form-detail-value">
                    {contact.phone}
                  </a>
                </div>
              </div>
              <div className="common-form-detail-item">
                <span className="common-form-detail-icon">
                  <IconMail />
                </span>
                <div className="common-form-detail-content">
                  <span className="common-form-detail-label">E-Mail</span>
                  <a href={`mailto:${contact.email}`} className="common-form-detail-value">
                    {contact.email}
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="common-form-container">
          <h3 id={isModal ? 'contact-modal-title' : undefined} className="common-form-card-title">{isModal ? 'Get In Touch' : 'Send a Message'}</h3>
            {submitStatus === 'success' && (
              <p className="common-form-status common-form-status--success">{successMessage}</p>
            )}
            {submitStatus === 'error' && (
              <p className="common-form-status common-form-status--error">{errorMessage}</p>
            )}
            {fieldError && (
              <p className="common-form-status common-form-status--error" role="alert">{fieldError}</p>
            )}
            <form className="common-form" onSubmit={handleSubmit} id={id} noValidate>
              <div className="common-form-row">
                <label htmlFor={`${id}-name`} className="common-form-field-label">
                  Name<span className="common-form-required">*</span>
                </label>
                <input
                  id={`${id}-name`}
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Name"
                  required
                />
                {errorField === 'name' && <span className="common-form-field-error">Required</span>}
              </div>
              <div className="common-form-row">
                <label htmlFor={`${id}-email`} className="common-form-field-label">
                  Email<span className="common-form-required">*</span>
                </label>
                <input
                  id={`${id}-email`}
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="E-mail address"
                  required
                />
                {(errorField === 'email' || errorField === 'email_format') && (
                  <span className="common-form-field-error">
                    {errorField === 'email' ? 'Required' : fieldErrorMessages.email_format}
                  </span>
                )}
              </div>
              {showOrganisationField && (
                <div className="common-form-row">
                  <label htmlFor={`${id}-organisation`} className="common-form-field-label">
                    Organisation name<span className="common-form-required">*</span>
                  </label>
                  <input
                    id={`${id}-organisation`}
                    type="text"
                    name="organisation"
                    value={formData.organisation || ''}
                    onChange={handleChange}
                    placeholder="Organisation name"
                    required
                  />
                  {errorField === 'organisation' && (
                    <span className="common-form-field-error">Required</span>
                  )}
                </div>
              )}
              <div className="common-form-row common-form-phone-row">
                <div className="common-form-phone-labels">
                  <span className="common-form-phone-label">
                    Phone<span className="common-form-required">*</span>
                  </span>
                </div>
                <div className="common-form-phone-wrap">
                  <PhoneInput
                    id={`${id}-phone`}
                    international
                    defaultCountry={DEFAULT_COUNTRY}
                    value={trimPhoneToMaxNationalDigits(formData.phone, DEFAULT_COUNTRY, MAX_NATIONAL_DIGITS)}
                    onChange={(value) => {
                      const next = trimPhoneToMaxNationalDigits(value || '', DEFAULT_COUNTRY, MAX_NATIONAL_DIGITS);
                      setFormData((prev) => ({ ...prev, phone: next }));
                      if (errorField === 'phone' || errorField === 'phone_length') setErrorField(null);
                    }}
                    placeholder="Phone number (7–12 digits)"
                    className="common-form-phone-input"
                    countryCallingCodeEditable={false}
                    numberInputProps={{
                      maxLength: 17,
                      inputMode: 'tel',
                    }}
                  />
                </div>
                {(errorField === 'phone' || errorField === 'phone_length') && (
                  <span className="common-form-field-error">
                    {errorField === 'phone' ? 'Required' : fieldErrorMessages.phone_length}
                  </span>
                )}
              </div>
              <div className="common-form-row">
                <label htmlFor={`${id}-message`} className="common-form-field-label">Message (optional)</label>
                <textarea
                  id={`${id}-message`}
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Message"
                  rows={3}
                />
              </div>
              <p className="common-form-privacy">{privacyText || defaultPrivacy}</p>
              <div className="common-form-actions">
                <button type="submit" className="common-form-submit">
                  Submit
                </button>
              </div>
            </form>
        </div>
      </div>
    </>
  );

  if (isModal) {
    return <div className="common-form-modal-inner">{formContent}</div>;
  }
  return (
    <section className="common-form-section" id="contact">
      <div className="common-form-wrapper">{formContent}</div>
    </section>
  );
};

export default Form;
